import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import MonitoringAlerts from '../components/MonitoringAlerts';
import { apiService } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`monitoring-tabpanel-${index}`}
      aria-labelledby={`monitoring-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Monitoring: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // エラー監視データ
  const [errorSummary, setErrorSummary] = useState<any>(null);
  const [errorLogs, setErrorLogs] = useState<any[]>([]);
  const [errorPage, setErrorPage] = useState(0);
  const [errorRowsPerPage, setErrorRowsPerPage] = useState(10);
  
  // 性能監視データ
  const [performanceSummary, setPerformanceSummary] = useState<any>(null);
  const [slowEndpoints, setSlowEndpoints] = useState<any[]>([]);
  
  // 監査ログデータ
  const [auditSummary, setAuditSummary] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(10);
  
  // ストレージデータ
  const [storageSummary, setStorageSummary] = useState<any>(null);
  
  // クリーンアップ状態
  const [cleanupStatus, setCleanupStatus] = useState<any>(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 各APIを並行して呼び出し
      const [errorData, perfData, auditData, storageData, cleanupData] = await Promise.all([
        apiService.getMonitoringErrorsSummary(),
        apiService.getMonitoringPerformanceSummary(),
        apiService.getMonitoringAuditSummary(),
        apiService.getMonitoringStorageSummary(),
        apiService.getMonitoringCleanupStatus(),
      ]);

      setErrorSummary(errorData as any);
      setPerformanceSummary(perfData as any);
      setAuditSummary(auditData as any);
      setStorageSummary(storageData as any);
      setCleanupStatus(cleanupData as any);

      // 詳細データも取得
      if (tabValue === 0) {
        await fetchErrorLogs();
      } else if (tabValue === 1) {
        await fetchSlowEndpoints();
      } else if (tabValue === 2) {
        await fetchAuditLogs();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchErrorLogs = async () => {
    try {
      const data = await apiService.getMonitoringErrors(errorRowsPerPage, errorPage * errorRowsPerPage) as any;
      setErrorLogs(data.errors || []);
    } catch (err) {
      console.error('Failed to fetch error logs:', err);
    }
  };

  const fetchSlowEndpoints = async () => {
    try {
      const data = await apiService.getMonitoringSlowEndpoints() as any;
      setSlowEndpoints(data || []);
    } catch (err) {
      console.error('Failed to fetch slow endpoints:', err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const data = await apiService.getMonitoringAuditLogs(auditRowsPerPage, auditPage * auditRowsPerPage) as any;
      setAuditLogs(data.audit_logs || []);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    }
  };

  const runCleanup = async (type: 'daily' | 'weekly') => {
    setCleanupRunning(true);
    try {
      await apiService.runMonitoringCleanup(type);
      await fetchData();
    } catch (err) {
      setError(`クリーンアップ実行に失敗しました: ${err}`);
    } finally {
      setCleanupRunning(false);
    }
  };

  const resolveError = async (errorId: string) => {
    try {
      await apiService.resolveMonitoringError(errorId);
      await fetchErrorLogs();
    } catch (err) {
      console.error('Failed to resolve error:', err);
    }
  };

  const markAuditReviewed = async (logId: string) => {
    try {
      await apiService.markAuditLogReviewed(logId);
      await fetchAuditLogs();
    } catch (err) {
      console.error('Failed to mark audit as reviewed:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon color="success" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'critical': return <ErrorIcon color="error" />;
      default: return <WarningIcon color="disabled" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>エラー</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">システム監視</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setAlertDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            アラート設定
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            更新
          </Button>
        </Box>
      </Box>

      {/* サマリーカード */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    エラー数（過去24時間）
                  </Typography>
                  <Typography variant="h4">
                    {errorSummary?.total_errors || 0}
                  </Typography>
                  <Typography variant="body2" color="error">
                    重要: {errorSummary?.critical_errors || 0}
                  </Typography>
                </Box>
                <ErrorIcon fontSize="large" color="error" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    平均レスポンス時間
                  </Typography>
                  <Typography variant="h4">
                    {performanceSummary?.avg_response_time_ms?.toFixed(0) || 0}ms
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    遅いリクエスト: {performanceSummary?.slow_requests_count || 0}
                  </Typography>
                </Box>
                <SpeedIcon fontSize="large" color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    監査ログ
                  </Typography>
                  <Typography variant="h4">
                    {auditSummary?.total_logs || 0}
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    レビュー待ち: {auditSummary?.review_required || 0}
                  </Typography>
                </Box>
                <SecurityIcon fontSize="large" color="secondary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    ストレージ使用率
                  </Typography>
                  <Typography variant="h4">
                    {storageSummary?.overall_usage_percentage?.toFixed(1) || 0}%
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    警告: {storageSummary?.warnings_count || 0}
                  </Typography>
                </Box>
                <StorageIcon fontSize="large" color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* タブパネル */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="エラー監視" icon={<ErrorIcon />} iconPosition="start" />
          <Tab label="性能監視" icon={<SpeedIcon />} iconPosition="start" />
          <Tab label="監査ログ" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="ストレージ" icon={<StorageIcon />} iconPosition="start" />
        </Tabs>

        {/* エラー監視タブ */}
        <TabPanel value={tabValue} index={0}>
          {errorSummary?.recent_critical_errors?.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>最近の重要エラー</AlertTitle>
              {errorSummary.recent_critical_errors.map((error: any) => (
                <Box key={error.id} mb={1}>
                  <Typography variant="body2">
                    {error.type}: {error.message} ({error.endpoint})
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDistanceToNow(new Date(error.created_at), { locale: ja, addSuffix: true })}
                  </Typography>
                </Box>
              ))}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>発生時刻</TableCell>
                  <TableCell>エラータイプ</TableCell>
                  <TableCell>エンドポイント</TableCell>
                  <TableCell>メッセージ</TableCell>
                  <TableCell>重要度</TableCell>
                  <TableCell>アクション</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {formatDistanceToNow(new Date(log.created_at), { locale: ja, addSuffix: true })}
                    </TableCell>
                    <TableCell>{log.error_type}</TableCell>
                    <TableCell>{log.endpoint || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.error_message}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.is_critical ? '重要' : '通常'}
                        color={log.is_critical ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {!log.resolved && (
                        <Tooltip title="解決済みにする">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => resolveError(log.id)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={errorSummary?.total_errors || 0}
            page={errorPage}
            onPageChange={(_, newPage) => setErrorPage(newPage)}
            rowsPerPage={errorRowsPerPage}
            onRowsPerPageChange={(e) => setErrorRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TabPanel>

        {/* 性能監視タブ */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    性能統計（過去24時間）
                  </Typography>
                  <Box>
                    <Typography variant="body2">
                      総リクエスト数: {performanceSummary?.total_requests || 0}
                    </Typography>
                    <Typography variant="body2">
                      平均レスポンス時間: {performanceSummary?.avg_response_time_ms?.toFixed(1) || 0}ms
                    </Typography>
                    <Typography variant="body2">
                      遅いリクエスト率: {performanceSummary?.slow_request_rate || '0%'}
                    </Typography>
                    <Typography variant="body2">
                      エラー率: {performanceSummary?.error_rate || '0%'}
                    </Typography>
                    <Typography variant="body2">
                      時間あたりリクエスト数: {performanceSummary?.requests_per_hour || 0}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    遅いエンドポイント
                  </Typography>
                  {slowEndpoints.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      遅いエンドポイントはありません
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>エンドポイント</TableCell>
                            <TableCell>平均時間</TableCell>
                            <TableCell>最大時間</TableCell>
                            <TableCell>回数</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {slowEndpoints.map((endpoint, index) => (
                            <TableRow key={index}>
                              <TableCell>{endpoint.endpoint}</TableCell>
                              <TableCell>{endpoint.avg_response_time_ms}ms</TableCell>
                              <TableCell>{endpoint.max_response_time_ms}ms</TableCell>
                              <TableCell>{endpoint.request_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* 監査ログタブ */}
        <TabPanel value={tabValue} index={2}>
          {auditSummary?.recent_critical_logs?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>最近の重要アクション</AlertTitle>
              {auditSummary.recent_critical_logs.map((log: any) => (
                <Box key={log.id} mb={1}>
                  <Typography variant="body2">
                    {log.action}: {log.description} ({log.user_email})
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDistanceToNow(new Date(log.created_at), { locale: ja, addSuffix: true })}
                  </Typography>
                </Box>
              ))}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>発生時刻</TableCell>
                  <TableCell>アクション</TableCell>
                  <TableCell>説明</TableCell>
                  <TableCell>ユーザー</TableCell>
                  <TableCell>重要度</TableCell>
                  <TableCell>レビュー</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {formatDistanceToNow(new Date(log.created_at), { locale: ja, addSuffix: true })}
                    </TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.description}
                    </TableCell>
                    <TableCell>{log.user_email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.severity}
                        color={getSeverityColor(log.severity) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {log.requires_review && !log.reviewed && (
                        <Tooltip title="レビュー済みにする">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => markAuditReviewed(log.id)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={auditSummary?.total_logs || 0}
            page={auditPage}
            onPageChange={(_, newPage) => setAuditPage(newPage)}
            rowsPerPage={auditRowsPerPage}
            onRowsPerPageChange={(e) => setAuditRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TabPanel>

        {/* ストレージタブ */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ストレージ概要
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="body2">
                      総使用量: {storageSummary?.total_usage_gb?.toFixed(2) || 0} GB / {storageSummary?.total_limit_gb || 0} GB
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={storageSummary?.overall_usage_percentage || 0}
                      color={
                        storageSummary?.overall_usage_percentage > 90
                          ? 'error'
                          : storageSummary?.overall_usage_percentage > 70
                          ? 'warning'
                          : 'primary'
                      }
                      sx={{ height: 10, borderRadius: 5, mt: 1 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    クリーンアップ
                  </Typography>
                  {cleanupStatus && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        ステータス: {cleanupStatus.is_running ? '実行中' : 'アイドル'}
                      </Typography>
                      {cleanupStatus.last_runs?.daily_cleanup && (
                        <Typography variant="body2" gutterBottom>
                          最終日次実行: {formatDistanceToNow(new Date(cleanupStatus.last_runs.daily_cleanup), { locale: ja, addSuffix: true })}
                        </Typography>
                      )}
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => runCleanup('daily')}
                          disabled={cleanupRunning || cleanupStatus.is_running}
                          sx={{ mr: 1 }}
                        >
                          日次クリーンアップ
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => runCleanup('weekly')}
                          disabled={cleanupRunning || cleanupStatus.is_running}
                        >
                          週次クリーンアップ
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {storageSummary?.warnings?.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <AlertTitle>ストレージ警告</AlertTitle>
                  {storageSummary.warnings.map((warning: any, index: number) => (
                    <Box key={index} mb={1}>
                      <Typography variant="body2">
                        {warning.tenant_name}: {warning.usage_gb}GB / {warning.limit_gb}GB ({warning.usage_percentage}%)
                      </Typography>
                    </Box>
                  ))}
                </Alert>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Paper>

      {/* アラート設定ダイアログ */}
      <MonitoringAlerts
        open={alertDialogOpen}
        onClose={() => setAlertDialogOpen(false)}
      />
    </Box>
  );
};

export default Monitoring;